import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, Search, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";

function bytesToHuman(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Files() {
  const { currentWorkspace } = useWorkspace();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    if (!currentWorkspace) return;
    const { data } = await api.get(`/files`, { params: { workspace_id: currentWorkspace.workspace_id } });
    setFiles(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace]);

  // Client-side filter — matches filename, uploader name, and content
  // type so a user searching "pdf" finds all PDFs and a user searching
  // "Joe" finds everything Joe uploaded.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => {
      const hay = [
        f.filename || "",
        f.uploaded_by_name || "",
        f.content_type || "",
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [files, query]);

  const upload = async (file) => {
    if (!file || !currentWorkspace) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("workspace_id", currentWorkspace.workspace_id);
    try {
      await api.post(`/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename || "file";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete ${f.filename}?`)) return;
    await api.delete(`/files/${f.file_id}`);
    toast.success("File deleted");
    load();
  };

  return (
    <div className="flex flex-col gap-6" data-testid="files-page">
      <header>
        <p className="bloq-eyebrow">Files</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Briefs, contracts, recordings — kept together.</h1>
      </header>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="bloq-card p-10 text-center cursor-pointer hover:bg-[#f5efe6] transition-colors"
        onClick={() => inputRef.current?.click()}
        data-testid="files-dropzone"
      >
        <Upload className="w-8 h-8 mx-auto text-[#82828c]" />
        <p className="font-semibold mt-3">{busy ? "Uploading…" : "Drag a file here, or click to upload"}</p>
        <p className="text-xs text-[#82828c] mt-1">Files are securely stored via Emergent Object Storage.</p>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="files-file-input"
        />
      </div>

      {/* Search bar — filters by filename, uploader, or content type.
          Matches the per-page search affordance used on Meetings, Notes,
          and Bookings so the UX is consistent across the app. */}
      {files.length > 0 && (
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search files by name, uploader, or type…"
            className="bloq-input pl-9 pr-9 w-full"
            data-testid="files-search-input"
            aria-label="Search files"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
              data-testid="files-search-clear"
              aria-label="Clear file search"
            >
              <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
            </button>
          )}
        </div>
      )}

      {files.length === 0 ? (
        <p className="text-sm text-[#82828c] text-center">No files yet.</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[#82828c] text-center" data-testid="files-empty-match">
          No files match &ldquo;{query}&rdquo;.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((f) => (
            <div key={f.file_id} className="bloq-card p-4 flex items-start gap-3" data-testid={`file-row-${f.file_id}`}>
              <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(0,94,122,0.08)" }}>
                {(f.content_type || "").startsWith("image/") ? <ImageIcon className="w-5 h-5 text-[#005e7a]" /> : <FileText className="w-5 h-5 text-[#005e7a]" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.filename}</p>
                <p className="text-xs text-[#82828c]">{bytesToHuman(f.size)} · {f.uploaded_by_name || "Someone"}</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => download(f)} className="bloq-btn-secondary text-xs px-2.5 py-1 inline-flex items-center gap-1" data-testid={`download-file-${f.file_id}`}><Download className="w-3 h-3" /> Download</button>
                  <button onClick={() => del(f)} className="bloq-btn-secondary text-xs px-2.5 py-1 inline-flex items-center gap-1 text-[#c43838]" data-testid={`delete-file-${f.file_id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
